/**
 * Sync service - PouchDB to CouchDB synchronization
 */

import PouchDB from 'pouchdb-browser';
import type { SyncState } from '../models/types.js';

type SyncHandler = PouchDB.Replication.Sync<{}>;

class SyncService {
  private localDB: PouchDB.Database;
  private remoteDB: PouchDB.Database | null = null;
  private syncHandler: SyncHandler | null = null;
  private state: SyncState = {
    isOnline: navigator.onLine,
    isSyncing: false,
    lastSyncTime: null,
    syncError: null
  };
  private listeners: Set<(state: SyncState) => void> = new Set();

  constructor(localDB: PouchDB.Database) {
    this.localDB = localDB;
    this.setupOnlineListener();
  }

  /**
   * Configure and start sync with remote CouchDB
   */
  async configureSyncUrl(url: string | null): Promise<void> {
    // Stop existing sync if any
    this.stopSync();

    if (!url) {
      this.remoteDB = null;
      this.emitStateChange();
      return;
    }

    try {
      // Create remote database connection
      this.remoteDB = new PouchDB(url, {
        skip_setup: false
      });

      // Test connection
      await this.remoteDB.info();
      console.log('Connected to remote database:', url);

      // Start syncing
      this.startSync();
    } catch (error) {
      console.error('Failed to connect to remote database:', error);
      this.updateState({
        syncError: `Failed to connect: ${(error as Error).message}`
      });
      this.remoteDB = null;
      throw error;
    }
  }

  /**
   * Start live bidirectional sync
   */
  private startSync(): void {
    if (!this.remoteDB) return;

    console.log('Starting live sync...');

    this.syncHandler = this.localDB.sync(this.remoteDB, {
      live: true,
      retry: true
    })
      .on('change', (info) => {
        console.log('Sync change:', info);
        this.updateState({
          isSyncing: false,
          lastSyncTime: Date.now(),
          syncError: null
        });
      })
      .on('paused', (err) => {
        console.log('Sync paused:', err);
        this.updateState({
          isSyncing: false
        });
      })
      .on('active', () => {
        console.log('Sync active');
        this.updateState({
          isSyncing: true,
          syncError: null
        });
      })
      .on('denied', (err) => {
        console.error('Sync denied:', err);
        this.updateState({
          isSyncing: false,
          syncError: 'Sync denied: check permissions'
        });
      })
      .on('complete', (info) => {
        console.log('Sync complete:', info);
        this.updateState({
          isSyncing: false,
          lastSyncTime: Date.now()
        });
      })
      .on('error', (err) => {
        console.error('Sync error:', err);
        this.updateState({
          isSyncing: false,
          syncError: (err as Error).message
        });
      });
  }

  /**
   * Stop active sync
   */
  stopSync(): void {
    if (this.syncHandler) {
      console.log('Stopping sync...');
      this.syncHandler.cancel();
      this.syncHandler = null;
      this.updateState({
        isSyncing: false
      });
    }
  }

  /**
   * Setup online/offline event listeners
   */
  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      console.log('Network online');
      this.updateState({ isOnline: true });
      if (this.remoteDB && !this.syncHandler) {
        this.startSync();
      }
    });

    window.addEventListener('offline', () => {
      console.log('Network offline');
      this.updateState({ isOnline: false });
    });
  }

  /**
   * Get current sync state
   */
  getState(): SyncState {
    return { ...this.state };
  }

  /**
   * Subscribe to sync state changes
   */
  subscribe(listener: (state: SyncState) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.getState());

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Update sync state and notify listeners
   */
  private updateState(updates: Partial<SyncState>): void {
    this.state = { ...this.state, ...updates };
    this.emitStateChange();
  }

  /**
   * Emit state change to all listeners
   */
  private emitStateChange(): void {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }

  /**
   * Manually trigger a one-time sync
   */
  async syncOnce(): Promise<void> {
    if (!this.remoteDB) {
      throw new Error('No remote database configured');
    }

    if (!this.state.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    try {
      this.updateState({ isSyncing: true, syncError: null });
      await this.localDB.sync(this.remoteDB);
      this.updateState({
        isSyncing: false,
        lastSyncTime: Date.now(),
        syncError: null
      });
    } catch (error) {
      this.updateState({
        isSyncing: false,
        syncError: (error as Error).message
      });
      throw error;
    }
  }
}

export default SyncService;
